import {
  type CreateTicketBody,
  MAX_TICKET_MESSAGE,
  TICKET_KIND_LABELS,
  TICKET_KINDS,
  TICKET_STATUS_LABELS,
  TICKET_STATUSES,
  type TicketDTO,
  type TicketKind,
  type TicketListResponse,
  type TicketStatus,
  type TicketSummaryDTO,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Inbox, NotebookPen, Paperclip, Send } from 'lucide-react';
import { type FormEvent, useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { RelativeTime } from '../../components/crm';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button, Card, CardHeader, EmptyState, Field, Input, Skeleton, Tabs, VoiceTextarea, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { api, apiUrl, errorMessage, fieldErrors } from '../../lib/api';
import { pluralize } from '../../lib/format';
import { TICKET_KIND_ICONS, TicketKindLabel, TicketStatusBadge } from './TicketBadges';
import { AttachmentDropZone, AttachmentPicker, ticketRequest } from './attachments';
import { useMessageDraft } from './useMessageDraft';

type StatusFilter = TicketStatus | 'all';

const STATUS_TABS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  ...TICKET_STATUSES.map((status) => ({ key: status, label: TICKET_STATUS_LABELS[status] })),
];

/** Notes: everyone sends ideas, problems and questions to the administrators and follows the replies. */
export function NotesPage() {
  const { can } = useAuth();
  const manage = can('ticket:manage');
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get('status');
  const status = manage ? TICKET_STATUSES.find((s) => s === param) : undefined;
  const query = { status };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tickets', 'list', query],
    queryFn: ({ signal }) => api.get<TicketListResponse>(apiUrl('/api/tickets', query), { signal }),
  });
  const items = data?.items ?? [];

  return (
    <Page width="narrow">
      <PageHeader
        title="Notes"
        description="Ideas, problems and questions for the administrator. Type or just talk — your voice is written out as text. Attach screenshots or files if they help."
      />

      <NoteComposer />

      <Card>
        <CardHeader
          title={manage ? 'Notes from the team' : 'Your notes'}
          description={manage ? 'Everyone’s notes, newest activity first.' : 'Open one to see the administrator’s reply.'}
        />
        {manage && (
          <Tabs
            aria-label="Filter by status"
            tabs={STATUS_TABS}
            value={status ?? 'all'}
            onChange={(key) => setSearchParams(key === 'all' ? {} : { status: key }, { replace: true })}
            className="px-4"
          />
        )}
        {isLoading ? (
          <div className="flex flex-col gap-2 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : isError ? (
          <p className="p-4 text-[13px] text-red-600">{errorMessage(error)}</p>
        ) : items.length === 0 ? (
          <EmptyState
            compact
            icon={manage && status ? Inbox : NotebookPen}
            title={manage && status ? `No notes marked ${TICKET_STATUS_LABELS[status]}` : 'No notes yet'}
            description={
              manage && status
                ? 'Try another status.'
                : 'Got an idea that would make Hearth better, or something that gets in your way? Write it above — or just talk.'
            }
          />
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((ticket) => (
              <li key={ticket.id}>
                <TicketRow ticket={ticket} showSender={manage} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Page>
  );
}

function TicketRow({ ticket, showSender }: { ticket: TicketSummaryDTO; showSender: boolean }) {
  return (
    <Link
      to={`/notes/${ticket.id}`}
      className="flex gap-3 px-4 py-3 transition-colors hover:bg-slate-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ember-500"
    >
      <span aria-hidden className={clsx('mt-1.5 size-2 shrink-0 rounded-full', ticket.unread ? 'bg-ember-500' : 'bg-transparent')} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <p className={clsx('min-w-0 text-[13px] break-words text-slate-900', ticket.unread ? 'font-semibold' : 'font-medium')}>
            <span className="font-normal text-slate-400 tabular">#{ticket.number}</span>{' '}
            {ticket.title}
            {ticket.unread && <span className="sr-only"> (new activity)</span>}
          </p>
          <TicketStatusBadge status={ticket.status} />
        </div>
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-slate-500">
          <TicketKindLabel kind={ticket.kind} />
          {showSender && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">{ticket.createdBy?.name ?? 'Former user'}</span>
            </>
          )}
          <span aria-hidden>·</span>
          <RelativeTime value={ticket.lastMessageAt} />
          <span aria-hidden>·</span>
          <span className="whitespace-nowrap">{pluralize(ticket.messageCount, 'message')}</span>
          {ticket.attachmentCount > 0 && (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-0.5 whitespace-nowrap" title={pluralize(ticket.attachmentCount, 'attachment')}>
                <Paperclip size={12} aria-hidden />
                <span aria-hidden>{ticket.attachmentCount}</span>
                <span className="sr-only">{pluralize(ticket.attachmentCount, 'attachment')}</span>
              </span>
            </>
          )}
        </p>
      </div>
    </Link>
  );
}

function NoteComposer() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const draft = useMessageDraft();
  const [kind, setKind] = useState<TicketKind>('feature');
  const [title, setTitle] = useState('');
  const kindName = useId();

  const create = useMutation({
    mutationFn: (request: CreateTicketBody | FormData) => api.post<TicketDTO>('/api/tickets', request),
    onSuccess: async (ticket) => {
      toast.success(`Thanks — note #${ticket.number} was sent to the administrator.`);
      setKind('feature');
      setTitle('');
      draft.reset();
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: (error) => toast.error(error),
  });
  const errors = fieldErrors(create.error);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (create.isPending) return;
    const { body, dictated, files } = draft.take();
    if (body.trim() === '') return;
    create.mutate(ticketRequest({ kind, title: title.trim() || null, body, dictated }, files));
  };

  return (
    <AttachmentDropZone onFiles={draft.addFiles} disabled={create.isPending}>
      <Card>
        <CardHeader title="Send a note" description="It goes to the administrator. Replies show up below and in the sidebar." />
        <form onSubmit={onSubmit} className="flex flex-col gap-4 p-4">
          <fieldset disabled={create.isPending}>
            <legend className="mb-1.5 text-[13px] font-medium text-slate-700">What is it?</legend>
            <div className="flex flex-wrap gap-1.5">
              {TICKET_KINDS.map((value) => {
                const Icon = TICKET_KIND_ICONS[value];
                return (
                  <label key={value} className="cursor-pointer">
                    <input type="radio" name={kindName} value={value} checked={kind === value} onChange={() => setKind(value)} className="peer sr-only" />
                    <span className="inline-flex h-9 items-center gap-1.5 rounded-full border border-slate-200 bg-surface px-3 text-[13px] font-medium text-slate-600 transition-colors select-none peer-checked:border-ember-300 peer-checked:bg-ember-50 peer-checked:text-ember-800 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ember-500 hover:border-slate-300 hover:text-slate-900 sm:h-8">
                      <Icon size={14} aria-hidden />
                      {TICKET_KIND_LABELS[value]}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
          <Field label="Title (optional)" error={errors.title}>
            <Input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={140} disabled={create.isPending} placeholder="A few words, e.g. “Map view for my leads”" />
          </Field>
          <Field label="Message" error={errors.body}>
            <VoiceTextarea
              {...draft.boxProps}
              rows={5}
              maxLength={MAX_TICKET_MESSAGE}
              disabled={create.isPending}
              placeholder="Describe your idea or what went wrong — or press Start voice typing and say it."
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) onSubmit(event);
              }}
            />
          </Field>
          <AttachmentPicker files={draft.files} onAdd={draft.addFiles} onRemove={draft.removeFile} disabled={create.isPending} />
          <div className="flex justify-end">
            <Button type="submit" variant="primary" icon={Send} loading={create.isPending} disabled={draft.empty} className="w-full max-sm:h-10 sm:w-auto">
              Submit ticket
            </Button>
          </div>
        </form>
      </Card>
    </AttachmentDropZone>
  );
}
